import { Button, Container, makeStyles } from '@material-ui/core';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import {
  BILLING_LOAD_STATUS,
  getAdditionalBillingDisplayStatus,
  LOAD_STATUS,
  ON_TIME_STATUS,
} from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import { AdditionalBillingLegData } from 'doc-mate-store/lib/models/AdditionalBillingLeg';
import { GPSPositionData, LatLng } from 'doc-mate-store/lib/models/GPSPosition';
import { LoadStopWithFlag } from 'doc-mate-store/lib/models/LoadStop';
import { OrderLeg, OrderLegData } from 'doc-mate-store/lib/models/OrderLeg';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router';

import AdditionalBillingDetail from '../../components/AdditionalBillingDetail';
import AdditionalBillingLoadDocument from '../../components/AdditionalBillingDocument';
import AdditionalBillingStatusAppBar from '../../components/AdditionalBillingStatusAppBar';
import CountdownTimer from '../../components/CountdownTimer';
import DriverPath from '../../components/DriverPath';
import LoadAppBar from '../../components/LoadAppBar';
import LoadStatusAppBar from '../../components/LoadStatusAppBar';
import RefreshNow from '../../components/RefreshNow';
import RouteMap from '../../components/RouteMap';
import TrackerAppBar from '../../components/TrackerAppBar';
import TrackerLoadDetail from '../../components/TrackerLoadDetail';
import TrackerLoadDetailMultiLeg from '../../components/TrackerLoadDetail/multiLeg';
import Root from '../Root';

type Props = {};

const LOAD_REFRESH_INTERVAL = 60;

const Track: React.FC<Props> = () => {
  const { rootStore } = useStores();
  // @ts-ignore
  const { orderLegLinkId } = useParams();
  const classes = useStyles();
  const [path, setPath] = useState<GPSPositionData[]>([]);
  const leg = rootStore.trackedLeg;

  const [currentLeg, setCurrentLeg] = useState<OrderLeg | undefined>(leg);
  const [loadStatus, setLoadStatus] = useState<OrderLegData | null | undefined>(
    undefined,
  );
  const [mapLoaded, setMapLoaded] = useState(false);

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const showAll = location.search.includes('show_all');

  const [orderLegData, setOrderLegData] = useState<OrderLegData[] | null>([]);

  const { me, loaded } = rootStore;
  const user = me ? me.maybeCurrent : undefined;
  const isDispatcher = user && user.isDispatcher;
  const isBroker = user && user.isBroker;
  const legStatus = leg ? leg.status : LOAD_STATUS.UNASSIGNED;
  const isDispatcherOrBroker = isDispatcher || isBroker;
  const isBilling = user && user.isBilling;
  const isDispatcherOrBilling = isDispatcher || isBilling;

  const [additionalBillingData, setAdditionalBillingData] = useState<
    AdditionalBillingLegData[]
  >([]);
  const [selectedBillingId, setSelectedBillingId] = useState<number | null>(
    null,
  );
  const selectedBillingLeg = selectedBillingId
    ? rootStore.additionalBillingLegs.get(`${selectedBillingId}`)
    : null;
  const [selectedTab, setSelectedTab] = useState<'legs' | 'billing'>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has('billing') ? 'billing' : 'legs';
  });

  // Wait for me/loaded before stripping ?billing and resetting tab
  useEffect(() => {
    if (!loaded) return;

    if (!isDispatcherOrBilling) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('billing')) {
        searchParams.delete('billing');
        const searchStr = searchParams.toString();
        const newUrl = searchStr
          ? `${window.location.pathname}?${searchStr}`
          : window.location.pathname;

        window.history.replaceState(null, '', newUrl);
        setSelectedTab('legs');
      }
    }
  }, [loaded, isDispatcherOrBilling]);

  const allLegs = rootStore.trackedAllLegs
    .sort((a, b) => Number(a.legNumber) - Number(b.legNumber))
    .sort((a, b) => Number(a.order.id) - Number(b.order.id));

  const currentActiveLeg = allLegs.find(
    leg =>
      leg.status !== LOAD_STATUS.DELIVERED &&
      leg.status !== LOAD_STATUS.COMPLETED,
  );

  const legsToShow = !isDispatcherOrBroker
    ? allLegs.filter(leg => leg.legNumber === 1)
    : allLegs;

  const legStops = useMemo(() => {
    if (!allLegs || allLegs.length === 0) return [];

    const filteredLegs =
      isDispatcherOrBroker || showAll
        ? allLegs
        : allLegs.filter(ol => ol.order.id === leg?.order.id);

    const stopsMap = new Map<string, LoadStopWithFlag>();

    const processStop = (orderLeg: OrderLeg, type: 'pickup' | 'dropoff') => {
      const stopData = type === 'pickup' ? orderLeg.pickup : orderLeg.dropoff;

      if (!stopData.isValid) return;

      if (!isDispatcherOrBroker && orderLeg.isMultiLeg) {
        if (type === 'pickup' && orderLeg.legNumber !== 1) return;
        if (type === 'dropoff' && orderLeg.legNumber === 1) return;
      }

      const locationId = stopData.current.location.id;
      const existing = stopsMap.get(locationId);

      if (existing) {
        const hasPickup = !!(
          existing.loadStop.pickupWindow?.lower ||
          (type === 'pickup' ? stopData.current.pickupWindow?.lower : null)
        );
        const hasDropoff = !!(
          existing.loadStop.dropoffWindow?.lower ||
          (type === 'dropoff' ? stopData.current.dropoffWindow?.lower : null)
        );

        stopsMap.set(locationId, {
          ...existing,
          isPickupDropff: hasPickup && hasDropoff,
        });
      } else {
        stopsMap.set(locationId, {
          loadStop: stopData.current,
          isPickupDropff: false,
        });
      }
    };

    filteredLegs.forEach(orderLeg => {
      processStop(orderLeg, 'pickup');
      processStop(orderLeg, 'dropoff');
    });

    return Array.from(stopsMap.values()).sort((a, b) => {
      const timeA = new Date(
        a.loadStop.pickupWindow?.lower || a.loadStop.dropoffWindow?.lower || 0,
      ).getTime();

      const timeB = new Date(
        b.loadStop.pickupWindow?.lower || b.loadStop.dropoffWindow?.lower || 0,
      ).getTime();

      return timeA - timeB;
    });
  }, [allLegs, isDispatcherOrBroker, leg?.order, showAll]);

  const handleStatusUpdated = useCallback(async () => {
    if (selectedBillingId) {
      await rootStore.fetchAdditionalBillingLeg(selectedBillingId);
    }
  }, [selectedBillingId, rootStore]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      if (orderLegLinkId) {
        const result = await rootStore.fetchOrderLegByOrderLegLinkId(
          orderLegLinkId,
        );
        setLoadStatus(result);

        const res = await rootStore.fetchAllOrderLegBillingLegByOrderLegLinkId(
          orderLegLinkId,
        );
        if (res) {
          const billingData = res.additionalBillingLegs || [];
          setOrderLegData(res.orderLegs || []);
          setAdditionalBillingData(billingData);

          const searchParams = new URLSearchParams(window.location.search);
          const billingParam = searchParams.get('billing');

          if (isDispatcherOrBilling && billingParam && billingData.length > 0) {
            const matchedBilling =
              billingData.find(
                b =>
                  String(b.id) === billingParam ||
                  b.dmId?.toLowerCase() === billingParam.toLowerCase() ||
                  b.billingType.toLowerCase() === billingParam.toLowerCase(),
              ) || billingData[0];

            if (matchedBilling) {
              setSelectedBillingId(matchedBilling.id);
            }
          }
        }
      }
      setLoading(false);
    })();

    return () => {
      rootStore.resetOrderLegLinkId();
    };
  }, [
    rootStore,
    orderLegLinkId,
    showAll,
    isDispatcherOrBroker,
    isDispatcherOrBilling,
  ]);

  useEffect(() => {
    if (!currentLeg) {
      setCurrentLeg(leg);
    }
  }, [currentLeg, leg]);

  useEffect(() => {
    if (leg) {
      leg.scheduleUpdateETA();
    }

    return () => {
      if (leg) {
        leg.clearUpdateETA();
      }
    };
  }, [currentLeg, leg, legStatus, rootStore]);

  const driverPosition = path.length > 0 ? path[path.length - 1] : undefined;

  if (driverPosition && currentActiveLeg) {
    currentActiveLeg.setCurrentGPSPosition(driverPosition);
  }

  const route = useMemo(() => {
    const result: LatLng[] = [];
    if (!leg) {
      return [];
    }
    if (driverPosition) {
      result.push({
        latitude: driverPosition.latitude,
        longitude: driverPosition.longitude,
      });
    }

    legStops.forEach(stop => {
      result.push(stop.loadStop.latlng);
    });
    return result;
  }, [driverPosition, leg, legStops]);

  const getTrackDisplayStatus = useCallback(
    (legItem: OrderLeg) => {
      if (
        !isDispatcherOrBilling &&
        BILLING_LOAD_STATUS.includes(legItem.status)
      ) {
        return 'Delivered';
      }

      const displayStatus = legItem.displayStatus;

      if (
        legItem.status === LOAD_STATUS.BILLING_ISSUE &&
        (displayStatus === 'Billing Issue - Missing' ||
          legItem.billingIssueDescription?.toLowerCase() === 'missing')
      ) {
        return 'Billing Issue - Missing Pages';
      }

      if (
        legItem.status === LOAD_STATUS.BILLING_ISSUE &&
        legItem.billingIssueDescription?.includes(',')
      ) {
        return 'Billing Issues - Other';
      }

      return displayStatus;
    },
    [isDispatcherOrBilling],
  );

  const handleRefreshLoad = useCallback(
    (mapLoaded: boolean = false) => {
      if (currentLeg) {
        if (currentLeg.isMultiLeg && !isDispatcherOrBroker) {
          rootStore.fetchAllOrderLegBillingLegByOrderLegLinkId(
            currentLeg.orderLegLinkId,
          );
        } else {
          rootStore.fetchOrderLegByOrderLegLinkId(currentLeg.orderLegLinkId);
        }
        if (!mapLoaded) {
          setMapLoaded(false);
        }
      }
    },
    [currentLeg, isDispatcherOrBroker, rootStore],
  );

  const handlePathInsert = useCallback((additionalPath: GPSPositionData[]) => {
    setPath(prevPath => [...prevPath, ...additionalPath]);
  }, []);

  const handlePathReset = useCallback((newPath: GPSPositionData[]) => {
    setPath(newPath);
  }, []);

  useEffect(() => {
    if (leg) {
      handleRefreshLoad(true);
    }
  }, [handleRefreshLoad, leg]);

  // Method 1 Guard: Early return loading view while store/me is initializing
  if (
    !loaded ||
    !leg ||
    typeof loadStatus === 'undefined' ||
    (leg &&
      (!leg.pickup ||
        !leg.pickup.isValid ||
        !leg.dropoff ||
        !leg.dropoff.isValid))
  ) {
    return (
      <Root>
        <Helmet>
          <title>
            {`Doc-Mate `}
            {isDispatcher ? 'Dispatcher' : isBroker ? 'Brokerage' : 'Tracker'}
          </title>
          <meta name="apple-itunes-app" content="app-id=1490860416" />
        </Helmet>
        <TrackerAppBar subtitle="Tracker" me={user} />
        <Container>Loading&hellip;</Container>
      </Root>
    );
  }

  const renderTopBar = () => {
    if (!user) {
      return null;
    }
    return <TrackerAppBar subtitle="Tracker" me={user} />;
  };

  const renderMapAreaBar = () => {
    if (user && currentLeg) {
      return <LoadAppBar loadId={currentLeg.dmId} showBackButton />;
    }
    return (
      <TrackerAppBar subtitle="Tracker" me={user}>
        {currentLeg && <LoadAppBar loadId={currentLeg.baseDmId} flex={2} />}
      </TrackerAppBar>
    );
  };

  const renderDispatcherLoadSelect = () => {
    if (isDispatcherOrBroker || (showAll && leg.isMultiOrder)) {
      return (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
          }}
        >
          <div style={{ textAlign: 'center', paddingTop: 10 }}>
            <span
              style={{
                fontSize: 24,
                fontWeight: 600,
                justifySelf: 'center',
                alignItems: 'center',
                color: '#101828',
              }}
            >
              MANIFEST # {leg.baseDmId}
            </span>
          </div>

          <div style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 4 }}>
            <ButtonGroup
              color="primary"
              aria-label="outlined primary button group"
              orientation="horizontal"
              size="small"
            >
              {!loading &&
                legsToShow.map(l => (
                  <Button
                    key={l.dmId}
                    variant={
                      selectedTab === 'legs' && l.dmId === currentLeg?.dmId
                        ? 'contained'
                        : 'outlined'
                    }
                    onClick={() => {
                      setSelectedTab('legs');
                      setCurrentLeg(l);
                      setMapLoaded(true);

                      const searchParams = new URLSearchParams(
                        window.location.search,
                      );
                      searchParams.delete('billing');
                      const searchStr = searchParams.toString();
                      const query = searchStr ? `?${searchStr}` : '';

                      window.history.replaceState(
                        null,
                        '',
                        `/l/${l.orderLegLinkId}/${query}`,
                      );
                    }}
                  >
                    {l.isMultiOrder && (showAll || isDispatcherOrBroker)
                      ? l.isMultiLeg && isDispatcherOrBroker
                        ? `O${l.orderNumber}/${l.orderCount} L${l.legNumber}/${l.splitLegCount}: `
                        : `O${l.orderNumber}/${l.orderCount}: `
                      : l.isMultiLeg && isDispatcherOrBroker
                      ? `L${l.legNumber}/${l.splitLegCount}: `
                      : ''}
                    {`${legStops.findIndex(
                      load =>
                        load.loadStop.location.id ===
                        l.pickup.current.location.id,
                    ) + 1}`}
                    <ArrowRightAltIcon style={{ marginBottom: 0.5 }} />
                    {!isDispatcherOrBroker && l.isMultiLeg
                      ? `${legStops.findIndex(
                          load =>
                            load.loadStop.location.id ===
                            allLegs.find(
                              legItem =>
                                legItem.order.id === l.order.id &&
                                legItem.legNumber === 2,
                            )?.dropoff.current.location.id,
                        ) + 1}`
                      : `${legStops.findIndex(
                          load =>
                            load.loadStop.location.id ===
                            l.dropoff.current.location.id,
                        ) + 1}`}
                  </Button>
                ))}
            </ButtonGroup>
          </div>

          {additionalBillingData?.length > 0 && isDispatcherOrBilling && (
            <div style={{ paddingBottom: 10, paddingLeft: 4 }}>
              <ButtonGroup
                color="primary"
                aria-label="outlined primary button group"
                orientation="horizontal"
                size="small"
              >
                {additionalBillingData.map(billing => {
                  const displayLabel = billing.dmId
                    ? `Billing: ${billing.dmId
                        .split('-')
                        .slice(1)
                        .join('-')}`
                    : `Billing: ${billing.billingType}`;

                  return (
                    <Button
                      key={billing.id}
                      variant={
                        selectedTab === 'billing' &&
                        selectedBillingLeg?.id === billing.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => {
                        setSelectedTab('billing');
                        setSelectedBillingId(billing.id);

                        const searchParams = new URLSearchParams(
                          window.location.search,
                        );
                        searchParams.set('billing', String(billing.id));
                        const searchStr = searchParams.toString();

                        window.history.replaceState(
                          null,
                          '',
                          `${window.location.pathname}?${searchStr}`,
                        );
                      }}
                    >
                      {displayLabel}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const renderStatusAppBar = () => {
    if (selectedTab === 'billing' && isDispatcherOrBilling) {
      if (selectedBillingLeg) {
        return (
          <AdditionalBillingStatusAppBar
            sticky
            loadStatus={getAdditionalBillingDisplayStatus(
              selectedBillingLeg.status as any,
            )}
            loadId={selectedBillingLeg.id}
            status={selectedBillingLeg.status as any}
            billingIssueDescription={selectedBillingLeg.billingIssueDescription}
            onStatusUpdated={handleStatusUpdated}
          />
        );
      }
    }

    if (currentLeg) {
      if (isDispatcherOrBroker || !currentLeg.isMultiLeg) {
        return (
          <LoadStatusAppBar
            sticky
            loadStatus={getTrackDisplayStatus(currentLeg)}
            onTimeStatus={currentLeg.onTimeStatus}
            loadId={currentLeg.id}
            status={currentLeg.status}
            onStatusUpdated={() => handleRefreshLoad(true)}
          />
        );
      } else {
        const sameOrderLegs = allLegs.filter(
          l => l.order.id === currentLeg.order.id,
        );
        const firstLeg = sameOrderLegs.find(l => l.legNumber === 1);
        const lastLeg = sameOrderLegs.find(
          l => l.legNumber === sameOrderLegs.length,
        );

        if (
          firstLeg &&
          (firstLeg.displayStatus === 'Unassigned' ||
            firstLeg.displayStatus === 'Pending Acceptance' ||
            firstLeg.displayStatus === 'Accepted' ||
            firstLeg.displayStatus === 'Load Started' ||
            firstLeg.displayStatus === 'Pickup accepted' ||
            firstLeg.displayStatus === 'En Route to Pickup' ||
            firstLeg.displayStatus === 'Arrived at Pickup' ||
            firstLeg.displayStatus === 'Checked in at Pickup' ||
            firstLeg.displayStatus === 'Departed from Pickup' ||
            firstLeg.displayStatus === 'En Route to Dropoff')
        ) {
          return (
            <LoadStatusAppBar
              sticky
              loadStatus={getTrackDisplayStatus(firstLeg)}
              onTimeStatus={firstLeg.onTimeStatus}
              loadId={firstLeg.id}
              status={firstLeg.status}
              onStatusUpdated={() => handleRefreshLoad(true)}
            />
          );
        } else if (
          lastLeg &&
          (lastLeg.displayStatus === 'En Route to Dropoff' ||
            lastLeg.displayStatus === 'Arrived at Dropoff' ||
            lastLeg.displayStatus === 'Delivered' ||
            lastLeg.displayStatus === 'Dispatch Cleared' ||
            BILLING_LOAD_STATUS.includes(lastLeg.status))
        ) {
          return (
            <LoadStatusAppBar
              sticky
              loadStatus={getTrackDisplayStatus(lastLeg)}
              onTimeStatus={lastLeg.onTimeStatus}
              loadId={lastLeg.id}
              status={lastLeg.status}
              onStatusUpdated={() => handleRefreshLoad(true)}
            />
          );
        } else {
          return (
            <LoadStatusAppBar
              sticky
              loadStatus={'Unknown'}
              onTimeStatus={ON_TIME_STATUS.UNKNOWN}
            />
          );
        }
      }
    }
  };

  const handleTrackerLoadDetail = () => {
    if (selectedTab === 'billing' && isDispatcherOrBilling) {
      if (!selectedBillingLeg) {
        return (
          <Container style={{ padding: 20 }}>
            Loading billing details...
          </Container>
        );
      }

      const billingLegModel = rootStore.additionalBillingLegs.get(
        `${selectedBillingLeg.id}`,
      );

      if (billingLegModel) {
        return (
          <>
            <AdditionalBillingDetail additionalBilling={billingLegModel} />
            <AdditionalBillingLoadDocument billingLeg={billingLegModel} />{' '}
          </>
        );
      }

      return (
        <Container style={{ padding: 20 }}>
          Loading billing document...
        </Container>
      );
    }

    if (currentLeg) {
      if (isDispatcherOrBroker || !currentLeg.isMultiLeg) {
        return (
          <TrackerLoadDetail
            load={currentLeg}
            otherLoads={
              currentLeg.isMultiOrder &&
              !currentLeg.shipper?.maybeCurrent?.name.includes('Metalsa')
                ? allLegs.filter(
                    a =>
                      currentLeg.id === a.id ||
                      currentLeg.legNumber === a.legNumber,
                  )
                : [currentLeg]
            }
            legStops={legStops}
          />
        );
      } else {
        if (!orderLegData) return;

        const sameOrderLegs = allLegs.filter(
          l => l.order.id === currentLeg.order.id,
        );
        const firstLeg = sameOrderLegs.find(l => l.legNumber === 1);
        const lastLeg = sameOrderLegs.find(
          l => l.legNumber === sameOrderLegs.length,
        );

        if (firstLeg && lastLeg) {
          return (
            <TrackerLoadDetailMultiLeg
              firstLoad={firstLeg}
              lastLoad={lastLeg}
            />
          );
        }
      }
    }
  };

  const renderRefresh = () => {
    return (
      <>
        {user ? (
          <RefreshNow onRefresh={() => handleRefreshLoad(false)} />
        ) : (
          <CountdownTimer
            defaultTime={LOAD_REFRESH_INTERVAL}
            onTimeElapsed={() => handleRefreshLoad(false)}
          />
        )}
      </>
    );
  };
  const renderLoadArea = () => {
    return (
      <div className={classes.loadArea}>
        {renderDispatcherLoadSelect()}
        {renderStatusAppBar()}
        {selectedTab !== 'billing' && renderRefresh()}

        {handleTrackerLoadDetail()}
      </div>
    );
  };

  return (
    <Root>
      <Helmet>
        <title>
          {`Manifest # ${leg.baseDmId} | Doc-Mate `}
          {isDispatcher ? 'Dispatcher' : isBroker ? 'Brokerage' : 'Tracker'}
        </title>
      </Helmet>

      <div className={classes.container}>
        {renderTopBar()}
        <div
          className={[
            classes.content,
            user ? classes.contentLoggedIn : undefined,
          ].join(' ')}
        >
          <div className={classes.mapArea}>
            {renderMapAreaBar()}
            <DriverPath
              loads={allLegs}
              onPathInsert={handlePathInsert}
              onPathReset={handlePathReset}
              legLinkId={leg.orderLegLinkId}
              onlyShowLinkId={
                leg.isMultiOrder && !showAll && !isDispatcherOrBroker
              }
              noReload={mapLoaded}
            />

            {!loading && leg && (
              <RouteMap
                className={classes.map}
                driverPosition={driverPosition}
                driverPath={path}
                stops={legStops}
                route={route}
                showRoute={isBroker || isDispatcher}
                selectedStop1={
                  currentLeg
                    ? currentLeg.isMultiLeg &&
                      !isDispatcherOrBroker &&
                      !!legStops.length
                      ? legStops[0].loadStop.location.id
                      : currentLeg.pickup.current.location.id
                    : ''
                }
                selectedStop2={
                  currentLeg && allLegs
                    ? currentLeg.isMultiLeg &&
                      !isDispatcherOrBroker &&
                      legStops.length > 1
                      ? (() => {
                          const foundLeg = allLegs.find(
                            item =>
                              item.order.id === currentLeg.order.id &&
                              item.legNumber === 2,
                          );
                          return foundLeg
                            ? foundLeg.dropoff.current.location.id
                            : '';
                        })()
                      : currentLeg.dropoff.current.location.id
                    : ''
                }
                noReload={mapLoaded}
              />
            )}
          </div>
          {renderLoadArea()}
        </div>
      </div>
    </Root>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100%',
    },
  },
  content: {
    flexGrow: 1,
    height: '100%',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  contentLoggedIn: {
    height: 'calc(100% - 48px)',
    marginTop: theme.spacing(6),
    '& $mapArea': {
      height: 'calc(80% - 48px)',
    },
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      '& $mapArea': {
        height: '100%',
      },
    },
  },
  loadArea: {
    background: 'white',
    position: 'absolute',
    top: '80%',
    left: 0,
    right: 0,
    [theme.breakpoints.up('md')]: {
      flex: 1,
      height: '100%',
      overflow: 'auto',
      maxWidth: 480,
      minWidth: 480,
      position: 'static',
    },
  },
  loadDetailArea: {
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      flex: 1,
    },
  },
  mapArea: {
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
    height: '80%',
    width: '100%',
    position: 'fixed',
    [theme.breakpoints.up('md')]: {
      position: 'static',
      height: '100%',
    },
  },
  map: {
    paddingTop: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      paddingTop: 0,
    },
  },
  refresh: {
    background: theme.palette.grey[100],
    margin: theme.spacing(1),
    padding: theme.spacing(0.5),
  },
}));

export default observer(Track);
